import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const VimeoPlayer = ({ vimeoId, title, autoplay = false, onReady, onPlay, onPause, onEnd, onTimeUpdate }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [playerError, setPlayerError] = useState(null);
  const playerRef = useRef(null);
  const playerInstanceRef = useRef(null);

  useEffect(() => {
    // Load Vimeo Player API script if not already loaded
    if (!window.Vimeo && !document.getElementById('vimeo-player-api')) {
      const script = document.createElement('script');
      script.id = 'vimeo-player-api';
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      script.onload = initializePlayer;
      script.onerror = () => setPlayerError('Failed to load Vimeo player');
      document.body.appendChild(script);
    } else {
      // If script already loaded, initialize player directly
      initializePlayer();
    }

    return () => {
      // Cleanup function - destroy player instance
      if (playerInstanceRef.current) {
        playerInstanceRef.current.destroy();
      }
    };
  }, [vimeoId]); // Re-initialize when vimeoId changes

  const initializePlayer = () => {
    if (!window.Vimeo || !playerRef.current) return;
    
    try {
      // Cleanup previous instance if it exists
      if (playerInstanceRef.current) {
        playerInstanceRef.current.destroy();
      }

      // Create new player instance
      playerInstanceRef.current = new window.Vimeo.Player(playerRef.current, {
        id: vimeoId,
        width: '100%',
        autopause: false,
        autoplay: autoplay,
        byline: false,
        portrait: false,
        title: false,
        dnt: true, // Do Not Track
        responsive: true
      });

      // Setup event listeners
      playerInstanceRef.current.on('loaded', handleReady);
      playerInstanceRef.current.on('play', handlePlay);
      playerInstanceRef.current.on('pause', handlePause);
      playerInstanceRef.current.on('ended', handleEnd);
      playerInstanceRef.current.on('timeupdate', handleTimeUpdate);
      playerInstanceRef.current.on('error', handleError);

      setIsLoading(false);
    } catch (error) {
      console.error('Error initializing Vimeo player:', error);
      setPlayerError('Error initializing video player');
      setIsLoading(false);
    }
  };

  const handleReady = () => {
    setIsLoading(false);
    if (onReady) onReady();
  };

  const handlePlay = () => {
    if (onPlay) onPlay();
  };

  const handlePause = () => {
    if (onPause) onPause();
  };

  const handleEnd = () => {
    if (onEnd) onEnd();
  };

  const handleTimeUpdate = (data) => {
    if (onTimeUpdate) onTimeUpdate(data);
  };

  const handleError = (error) => {
    console.error('Vimeo player error:', error);
    setPlayerError('An error occurred while playing the video');
  };

  return (
    <div className="vimeo-player-container w-full">
      {isLoading && (
        <div className="flex items-center justify-center bg-gray-100 rounded-md" style={{ aspectRatio: '16/9' }}>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mb-2"></div>
            <p className="text-gray-600">Loading video player...</p>
          </div>
        </div>
      )}

      {playerError && (
        <div className="flex items-center justify-center bg-red-50 text-red-700 rounded-md p-4" style={{ aspectRatio: '16/9' }}>
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-red-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-medium">{playerError}</p>
            <p className="text-sm mt-1">Please try refreshing the page or contact support if the issue persists.</p>
          </div>
        </div>
      )}

      <div 
        ref={playerRef} 
        className={`vimeo-player w-full ${isLoading ? 'hidden' : 'block'}`}
        style={{ aspectRatio: '16/9' }}
        aria-label={`Vimeo video player for ${title}`}
      ></div>

      {/* Video title caption - optional */}
      {title && !isLoading && !playerError && (
        <div className="mt-2 text-sm text-gray-600 px-2">
          {title}
        </div>
      )}
    </div>
  );
};

VimeoPlayer.propTypes = {
  vimeoId: PropTypes.string.isRequired,
  title: PropTypes.string,
  autoplay: PropTypes.bool,
  onReady: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnd: PropTypes.func,
  onTimeUpdate: PropTypes.func
};

export default VimeoPlayer;